import { MinusCircle } from 'lucide-react';

export interface DotVotingLiveItem {
  id: string;
  name: string;
  description?: string;
  votes: number;
}

interface DotVotingLiveProps {
  items: DotVotingLiveItem[];
  dotsPerParticipant: number;
  remainingDots: number;
  onVote: (itemId: string) => void;
  onRemoveVote: (itemId: string) => void;
}

export function DotVotingLive({
  items,
  dotsPerParticipant,
  remainingDots,
  onVote,
  onRemoveVote,
}: DotVotingLiveProps) {
  const used = dotsPerParticipant - remainingDots;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 font-heading text-[0.55rem] tracking-[0.08em] uppercase text-[var(--text-ter)]">
        <span>
          <span className="text-electric-mustard-600 font-bold">{remainingDots}</span> dots left
        </span>
        <span>
          {used} / {dotsPerParticipant} placed
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map(item => {
          const hasVotes = item.votes > 0;
          return (
            <div
              key={item.id}
              className={` border px-3 py-3 transition-colors ${
                hasVotes
                  ? 'border-alpine-green-500/45 bg-alpine-green-500/10'
                  : 'border-[var(--rule-bright)] bg-[var(--surface)]'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-body text-[0.82rem] font-medium text-[var(--text-sec)]">{item.name}</div>
                  {item.description && (
                    <div className="font-body text-[0.7rem] font-light text-[var(--text-ter)] mt-1 line-clamp-2">
                      {item.description}
                    </div>
                  )}
                </div>
                {/* Dot pips */}
                <div className="flex flex-wrap gap-1 flex-shrink-0 justify-end max-w-[72px]">
                  {Array.from({ length: item.votes }).map((_, i) => (
                    <span key={i} className="w-2.5 h-2.5 rounded-full bg-electric-mustard-600 block" />
                  ))}
                </div>
              </div>

              {/* +/- controls */}
              <div className="flex items-center gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => onVote(item.id)}
                  disabled={remainingDots === 0}
                  className="flex-1 flex items-center justify-center gap-1 rounded px-2 py-1.5 text-[0.65rem] font-heading tracking-[0.08em] uppercase bg-electric-mustard-600/10 border border-electric-mustard-600/30 text-electric-mustard-600 hover:bg-electric-mustard-600/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  + Add dot
                </button>
                {hasVotes && (
                  <button
                    type="button"
                    onClick={() => onRemoveVote(item.id)}
                    className="flex items-center justify-center gap-1 rounded px-2 py-1.5 text-[0.65rem] font-heading tracking-[0.08em] uppercase bg-[var(--rule)] border border-[var(--surface-raised)] text-[var(--text-ter)] hover:border-luminous-coral-700/40 hover:text-luminous-coral-400 transition-colors"
                    aria-label="Remove one dot"
                  >
                    <MinusCircle className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="font-heading text-[0.48rem] tracking-[0.1em] uppercase text-[var(--text-dim)] text-center mt-4">
        Tap + to add a dot · tap − to remove one · use all dots before submitting
      </p>
    </div>
  );
}
